import React from 'react'
import debounce from 'lodash.debounce'

export enum SAVING_STATE {
  SAVING = 'Saving...',
  SAVED = 'Saved',
}

export const SHOW_SAVING_DELAY = 500
export const SHOW_SAVED_DELAY = 3000

export const useSavingIndicator = (
  setSavingText: (savingText: SAVING_STATE | null) => void,
): (() => void) => {
  const resetSavingTextTimeoutId = React.useRef<number | null>(null)

  const resetSavingText = React.useCallback(() => {
    setSavingText(null)
  }, [setSavingText])

  const debouncedSavedIndicator = React.useMemo(
    () =>
      debounce(() => {
        setSavingText(SAVING_STATE.SAVED)

        resetSavingTextTimeoutId.current = window.setTimeout(
          resetSavingText,
          SHOW_SAVED_DELAY,
        )
      }, SHOW_SAVING_DELAY),
    [resetSavingText, resetSavingTextTimeoutId, setSavingText],
  )

  const inidicateSaving = React.useCallback(() => {
    // Reset timeout since back to saving state
    if (resetSavingTextTimeoutId.current) {
      window.clearTimeout(resetSavingTextTimeoutId.current)
    }

    setSavingText(SAVING_STATE.SAVING)
    debouncedSavedIndicator()
  }, [debouncedSavedIndicator, resetSavingTextTimeoutId, setSavingText])

  React.useEffect(() => {
    // Set initial state on mount
    setSavingText(null)

    // Cancel all the things on unmount
    return () => {
      debouncedSavedIndicator.cancel()

      if (resetSavingTextTimeoutId.current) {
        window.clearTimeout(resetSavingTextTimeoutId.current)
      }
    }
  }, [debouncedSavedIndicator, resetSavingTextTimeoutId, setSavingText])

  return inidicateSaving
}

export default useSavingIndicator
